import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Phone, Mail, Calendar, Building2, Activity, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';
import { LEAD_STAGES } from './constants';
import { HealthIndicator, ScoreBadge } from './LeadCardVisuals';

/**
 * LeadDetailModal — 4-tab drawer (Overview / Scoring / Activity / AI).
 *
 * Receives onRefresh so mutations (add-activity, stage change) can force
 * a re-fetch of just this one lead — see LeadsPipelinePage.refreshSingleLead.
 */
export const LeadDetailModal = ({ lead, open, onClose, onUpdate, onDelete, onRefresh }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (lead) {
      setFormData({
        name: lead.name || '',
        email: lead.email || '',
        phone: lead.phone || '',
        company: lead.company || '',
        job_title: lead.job_title || '',
        notes: lead.notes || '',
        stage: lead.stage || 'raw',
      });
    }
  }, [lead]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(lead.lead_id, formData);
      setEditing(false);
      toast.success('Lead updated successfully');
    } catch (error) {
      toast.error('Failed to update lead');
    } finally {
      setSaving(false);
    }
  };

  const handleStageChange = async (newStage) => {
    try {
      await api.put(`/leads/${lead.lead_id}/stage`, { stage: newStage });
      if (onRefresh) await onRefresh(lead.lead_id);
      else onUpdate(lead.lead_id, { ...formData, stage: newStage });
      setFormData((prev) => ({ ...prev, stage: newStage }));
      toast.success(`Lead moved to ${newStage}`);
    } catch (error) {
      toast.error('Failed to update stage');
    }
  };

  const handleAddActivity = async (type) => {
    try {
      await api.post(`/leads/${lead.lead_id}/activity`, {
        activity_type: type,
        description: `${type} logged`,
      });
      if (onRefresh) await onRefresh(lead.lead_id);
      toast.success('Activity logged');
    } catch (error) {
      toast.error('Failed to log activity');
    }
  };

  if (!lead) return null;

  const score = lead.calculated_score || 0;
  const engagementScore = lead.engagement_score || 0;
  const intentScore = lead.intent_score || 0;
  const profileScore = lead.profile_fit_score || 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="border-b border-slate-200 pb-4">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-xl">
                {lead.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div>
                <DialogTitle className="text-xl">{lead.name}</DialogTitle>
                <DialogDescription className="flex items-center gap-2 mt-1">
                  {lead.company && (<><Building2 className="w-4 h-4" /><span>{lead.company}</span></>)}
                  {lead.job_title && <span className="text-slate-400">• {lead.job_title}</span>}
                </DialogDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <HealthIndicator health={lead.health} />
              <ScoreBadge score={score} />
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full justify-start border-b rounded-none bg-transparent px-0 mb-4">
              <TabsTrigger value="overview" className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none">Overview</TabsTrigger>
              <TabsTrigger value="scoring" className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none">Scoring</TabsTrigger>
              <TabsTrigger value="activity" className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none">Activity</TabsTrigger>
              <TabsTrigger value="ai" className="data-[state=active]:border-b-2 data-[state=active]:border-blue-600 rounded-none">AI Insights</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6 mt-0">
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => handleAddActivity('call')}><Phone className="w-4 h-4 mr-1" /> Call</Button>
                <Button size="sm" variant="outline" onClick={() => handleAddActivity('email')}><Mail className="w-4 h-4 mr-1" /> Email</Button>
                <Button size="sm" variant="outline" onClick={() => handleAddActivity('meeting')}><Calendar className="w-4 h-4 mr-1" /> Schedule</Button>
                <div className="flex-1" />
                <Select value={lead.stage} onValueChange={handleStageChange}>
                  <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LEAD_STAGES.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.emoji} {s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center justify-between">
                    Contact Information
                    <Button size="sm" variant="ghost" onClick={() => setEditing(!editing)}>{editing ? 'Cancel' : 'Edit'}</Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  {editing ? (
                    <>
                      <div><Label className="text-xs text-slate-500">Name</Label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="mt-1" /></div>
                      <div><Label className="text-xs text-slate-500">Company</Label><Input value={formData.company} onChange={(e) => setFormData({ ...formData, company: e.target.value })} className="mt-1" /></div>
                      <div><Label className="text-xs text-slate-500">Email</Label><Input value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="mt-1" /></div>
                      <div><Label className="text-xs text-slate-500">Phone</Label><Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="mt-1" /></div>
                      <div className="col-span-2"><Button onClick={handleSave} disabled={saving} className="w-full">{saving ? 'Saving...' : 'Save Changes'}</Button></div>
                    </>
                  ) : (
                    <>
                      <div><Label className="text-xs text-slate-500">Email</Label><p className="text-sm text-slate-900">{lead.email || '-'}</p></div>
                      <div><Label className="text-xs text-slate-500">Phone</Label><p className="text-sm text-slate-900">{lead.phone || '-'}</p></div>
                      <div><Label className="text-xs text-slate-500">Source</Label><p className="text-sm text-slate-900 capitalize">{lead.source?.replace('_', ' ') || '-'}</p></div>
                      <div><Label className="text-xs text-slate-500">Created</Label><p className="text-sm text-slate-900">{lead.created_at ? new Date(lead.created_at).toLocaleDateString() : '-'}</p></div>
                    </>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Notes</CardTitle></CardHeader>
                <CardContent><Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Add notes about this lead..." rows={3} /></CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="scoring" className="space-y-4 mt-0">
              <div className="grid grid-cols-3 gap-4">
                <Card className="bg-blue-50 border-blue-100"><CardContent className="pt-4"><div className="text-center"><p className="text-xs text-blue-600 font-medium mb-1">Engagement</p><p className="text-3xl font-bold text-blue-700">{Math.round(engagementScore)}</p><p className="text-xs text-blue-500 mt-1">30% weight</p></div></CardContent></Card>
                <Card className="bg-purple-50 border-purple-100"><CardContent className="pt-4"><div className="text-center"><p className="text-xs text-purple-600 font-medium mb-1">Intent</p><p className="text-3xl font-bold text-purple-700">{Math.round(intentScore)}</p><p className="text-xs text-purple-500 mt-1">40% weight</p></div></CardContent></Card>
                <Card className="bg-emerald-50 border-emerald-100"><CardContent className="pt-4"><div className="text-center"><p className="text-xs text-emerald-600 font-medium mb-1">Profile Fit</p><p className="text-3xl font-bold text-emerald-700">{Math.round(profileScore)}</p><p className="text-xs text-emerald-500 mt-1">30% weight</p></div></CardContent></Card>
              </div>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div><p className="text-sm text-slate-600">Total Lead Score</p><p className="text-4xl font-bold text-slate-900">{Math.round(score)}/100</p></div>
                    <div className="w-32 h-32 relative">
                      <svg className="w-full h-full transform -rotate-90">
                        <circle cx="64" cy="64" r="56" stroke="#e2e8f0" strokeWidth="12" fill="none" />
                        <circle cx="64" cy="64" r="56" stroke={score >= 80 ? '#10b981' : score >= 60 ? '#3b82f6' : score >= 40 ? '#f59e0b' : '#64748b'} strokeWidth="12" fill="none" strokeDasharray={`${(score / 100) * 352} 352`} strokeLinecap="round" />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center"><span className="text-2xl font-bold">{Math.round(score)}</span></div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="activity" className="mt-0">
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {lead.activities?.length > 0 ? (
                  lead.activities.slice().reverse().map((activity, idx) => (
                    <div key={activity.activity_id || activity.created_at || `act-${idx}`} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center"><Activity className="w-4 h-4 text-blue-600" /></div>
                      <div>
                        <p className="text-sm text-slate-900">{activity.description}</p>
                        <p className="text-xs text-slate-500 mt-1">{activity.created_at ? new Date(activity.created_at).toLocaleString() : ''}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-slate-400"><Activity className="w-8 h-8 mx-auto mb-2 opacity-50" /><p>No activity recorded yet</p></div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="ai" className="space-y-4 mt-0">
              <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100">
                <CardHeader><CardTitle className="text-sm font-medium flex items-center gap-2"><Sparkles className="w-4 h-4 text-blue-600" /> AI Prediction</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><p className="text-xs text-slate-500">Close Probability</p><p className="text-2xl font-bold text-blue-700">{Math.round(lead.ai_prediction?.close_probability || 0)}%</p></div>
                    <div><p className="text-xs text-slate-500">Expected Revenue</p><p className="text-2xl font-bold text-emerald-700">${(lead.ai_prediction?.expected_revenue || 0).toLocaleString()}</p></div>
                  </div>
                  <div className="bg-white rounded-lg p-3"><p className="text-xs text-slate-500 mb-1">Recommended Next Action</p><p className="text-sm font-medium text-blue-700">{lead.ai_prediction?.next_best_action || 'Contact lead'}</p></div>
                  <div className="flex items-center gap-2">
                    <Badge variant={lead.ai_prediction?.confidence_level === 'high' ? 'default' : lead.ai_prediction?.confidence_level === 'medium' ? 'secondary' : 'outline'}>
                      {lead.ai_prediction?.confidence_level || 'low'} confidence
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        <div className="border-t border-slate-200 pt-4 flex items-center justify-between">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <div className="flex items-center gap-2">
            <Button variant="destructive" size="sm" onClick={() => onDelete(lead.lead_id)}>Delete Lead</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
